import React from "react";
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

import ActionButtons from "./ActionButtons";
import logo from "../../images/logo.png";
import agentAvatar from "../../images/logo.png"; // Adjust if you have a separate agent avatar
import Attachment from "./Attachment";
import getValueOrUndefined from "../../util/getValueOrUndefined";
import SuggestionActionList from "./SuggestedActionList";
import { BiSupport } from "react-icons/bi";
import SourceList from "./SourceList";
import PersonIcon from "../../images/personicon.svg";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FaRegCopy, FaCheck } from "react-icons/fa";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "../ui/tooltip";
const BotThinking = ({ visible, message = "Rammas is thinking", isArabic }) => {
  const localizedMessage = isArabic ? "رماس يفكر" : message;

  const keyframes = `
    @keyframes wave {
      0%, 20% { opacity: 0.3; }
      50% { opacity: 1; }
      100% { opacity: 0.3; }
    }
  `;

  const textAndDotStyle = {
    display: "inline-block",
    color: "#007560",
    fontSize: "18px",
    fontWeight: "500",
    animationName: "wave",
    animationDuration: "2s",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    opacity: visible ? 1 : 0,
    transition: "opacity 1s ease-in-out",
  };

  const textArray = ["●", "●", "●"];

  const containerStyle = {
    direction: isArabic ? "rtl" : "ltr",
  };

  const messageStyle = {
    marginRight: isArabic ? "0" : "8px",
    marginLeft: isArabic ? "8px" : "0",
    opacity: visible ? 1 : 0,
    transition: "opacity 1s ease-in-out",
  };

  const dotStyle = (index) => ({
    ...textAndDotStyle,
    animationDelay: `${index * 0.1}s`,
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#000000",
    margin: "0 2px",
  });

  return (
    <div className="flex items-center" style={containerStyle}>
      <style>{keyframes}</style>
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <span style={messageStyle}>{localizedMessage}</span>
        {textArray.map((char, index) => (
          <span key={index} style={dotStyle(index)}>
            {char !== "●" ? (char === " " ? "\u00A0" : char) : ""}
          </span>
        ))}
      </span>
    </div>
  );
};
const MessageItem = ({
  activity,
  sources,
  id,
  responseGood,
  suggestedActionlist,
  onSuggestionClick,
  isArabic,
  surveyData,
  disableSend,
  locked
}) => {
  const arabicRegex = /[\u0600-\u06FF]/; // Arabic character range
  const numericRegex = /^-?\d+(\.\d+)?$/; // Matches a string with only numbers

  const [script, setScript] = useState("");
  const [copied, setCopied] = useState(false);

  const handleActionClick = (actionType) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 5000);
  };

  const isTyping = activity.type === "typing";
  const text = isTyping
    ? activity.text
    : getValueOrUndefined(activity, "channelData", "messageBack", "displayText") || activity.text;

  const isUser = activity.from.role === "user";
  const isAgent = activity.channelData.role === "agent";

  const getLocal = (text) => {
    if (isArabic && numericRegex.test(text)) {
      return "ar-EG";
    }
    if (isArabic && !text) {
      return "ar-EG";
    }
    return arabicRegex.test(text) ? "ar-EG" : "en-GB";
  };

  const isArabicText = (text) => {
    if (isArabic && typeof text === "string" && text.split("|||").length > 1) {
      const parts = text.split("|||");
      return arabicRegex.test(parts[1] ?? "");
    }
    return arabicRegex.test(text?.split("|||")[0] ?? "");
  };

  const convertArabicToEnglishDigits = (str) => {
    const arabicDigits = "٠١٢٣٤٥٦٧٨٩"; // Arabic digits
    const englishDigits = "0123456789"; // English digits
    return str
      .split("")
      .map((char) => {
        const index = arabicDigits.indexOf(char);
        return index !== -1 ? englishDigits[index] : char;
      })
      .join("");
  };

  const timestamp =
    new Date(activity.timestamp)
      .toLocaleDateString(getLocal(text), {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .split("")
      .map((char) => convertArabicToEnglishDigits(char)) // Convert Arabic digits to English
      .join("") +
    " " +
    new Date(activity.timestamp)
      .toLocaleTimeString(getLocal(text), {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      })
      .split("")
      .map((char) => convertArabicToEnglishDigits(char)) // Convert Arabic digits to English
      .join("");

  const messageStyles = isUser
    ? "bg-[#e0eeec] shadow-lg" // User message style
    : isAgent
    ? "bg-blue-100" // Agent message style
    : "bg-red "; // Bot message style

  const avatarSrc = isAgent ? agentAvatar : logo; // Set avatar based on the sender

  const getDirection = (text) => {
    if (isArabic && !text) {
      return "rtl";
    }
    if (isArabic && numericRegex.test(text)) {
      return "rtl";
    }
    return arabicRegex.test(text) ? "rtl" : "ltr";
  };

  const cardChosen = activity?.attachments?.length > 0
    ? isArabic && activity?.attachments?.length > 1
      ? activity?.attachments[1]
      : activity?.attachments[0]
    : null;

  const isMobile = window.innerWidth <= 1000; // Check if the screen size is mobile
  const fontSize = isMobile ? "0.8rem" : "16px"; // Adjust as needed

  return (
    <li
      className={`flex flex-col ${
        isUser ? "items-end" : "items-start"
      } p-0 m-0`}
      style={{
        textAlign: getDirection(text) === "rtl" ? "right" : "left",
        direction: getDirection(text), // Set direction for Arabic text
      }}
    >
      <div
        className={`flex ${isUser ? "justify-end" : "justify-start"} w-full`}
      >
        {isUser ? (
          <div className="flex flex-col items-end">
            <div
              className={`inline-block max-w-max md:max-w-lg w-auto p-2 ${messageStyles} ml-12 text-right rounded-lg`}
              style={{
                background: isTyping ? "rgba(0, 117, 96, 0.12)" : undefined,
              }}
            >
              {isTyping ? (
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-gray-300 animate-pulse mr-2"></div>
                  <span className="text-gray-600">{text}</span>
                </div>
              ) : (
<ReactMarkdown
  className="break-words"
  remarkPlugins={[remarkGfm]}
  components={{
    a: ({ node, children, ...props }) => (
      <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#007560",
          textDecoration: "underline",
        }}
      >
        {children || props.href}
      </a>
    ),
    li: ({ node, children, ...props }) => (
      <li
        {...props}
        style={{
          // color: "#333", // Customize the text color
          // fontSize: "1rem", // Customize the font size
          marginBottom: "0.5rem", // Add spacing between list items
          marginLeft:'1.5rem',
          listStyleType: "decimal", // Ensure ordered list style
        }}
      >
        {children?.length > 0 ? children : <>&nbsp;</>}
        {/* Render a non-breaking space if the list item is empty */}
      </li>
    ),
  }}
>
  {text}
</ReactMarkdown>

              )}
              {activity?.attachments?.length > 0 && (
                <ul className="mt-2 space-y-2">
                  {activity?.attachments?.map((attachment, index) => (
                    <li key={index}>
                      <Attachment {...attachment} isArabic={isArabic} />
                    </li>
                  ))}
                </ul>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleActionClick("copy")}
                      className="text-gray-500 p-1"
                    >
                      {copied ? (
                        <FaCheck className="w-4 h-5" />
                      ) : (
                        <FaRegCopy className="w-3 h-4" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{copied ? "Copied!" : "Copy"}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {/* Ensure it starts from the right */}
            <div
              className={`flex items-center w-full mt-1 ${
                isArabicText(text) || isArabic
                  ? "flex-row-reverse justify-end"
                  : "flex-row justify-end"
              }`}
            >
              <span
                className="text-gray-500 text-sm"
                style={{
                  marginLeft: isArabicText(text) || isArabic ? "8px" : "0",
                  marginRight: isArabicText(text) || isArabic ? "0" : "8px",
                  fontSize,
                }}
              >
                {timestamp}
              </span>
              <Avatar className="flex items-center">
                <AvatarImage
                  className="w-[25px] h-[25px] rounded-full object-cover opacity-75"
                  src={PersonIcon}
                />
              </Avatar>
            </div>
          </div>
        ) : (
          <div
            className="flex  flex-row  items-start"
          >
            <div className="flex items-center  flex-row mb-1">
              {isAgent ? (
                <BiSupport className="w-[25px] h-[25px] rounded-full object-cover" />
              ) : (
                <Avatar className="flex items-center">
                  <AvatarImage
                    className="w-[25px] h-[25px] rounded-full object-cover"
                    src={avatarSrc}
                    alt={isAgent ? "Agent Avatar" : "Bot Avatar"} // Set alt based on sender
                  />
                  <AvatarFallback className="absolute">
                    {isAgent ? "A" : "R"}
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
            <div
              className={`inline-block p-2 ${messageStyles}  mr-auto  rounded-lg`}
            >
              {text === "Bot is thinking..." || text === "البوت يفكر..." ? (
                <BotThinking visible={true} isArabic={isArabic} />
              ) : (
                <div>
                  {isTyping ? (
                    <div
                      className="flex items-center"
                      dir={getDirection(text)} // Set direction dynamically based on content
                    >
                  <ReactMarkdown
  className="break-words"
  remarkPlugins={[remarkGfm]}
  components={{
    a: ({ node, children, ...props }) => (
      <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#007560",
          textDecoration: "underline",
        }}
      >
        {children || props.href}
      </a>
    ),
  }}
>
  {text}
</ReactMarkdown>

                    </div>
                  ) : (
                    <>
             <ReactMarkdown
  className="break-words"
  remarkPlugins={[remarkGfm]}
  components={{
    a: ({ node, children, ...props }) => (
      <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#007560",
          textDecoration: "underline",
        }}
      >
        {children || props.href}
      </a>
    ),
  }}
>
  {text}
</ReactMarkdown>


                      {cardChosen && (
                        <Attachment {...cardChosen} disableSend={disableSend} setScript={setScript} isArabic={isArabicText(text)} id={id} surveyData={surveyData} />
                      )}
                      <span
                        className="text-gray-500 "
                        style={{ fontSize }}
                      >
                        {timestamp}
                      </span>
                      {!isAgent && (
                        <ActionButtons
                          activity={activity}
                          text={text}
                          id={id}
                          responseGood={responseGood}
                          script={script}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {sources && sources?.length > 0 && (
        <div className={`mt-2  ${isArabic ? "mr-12" : "ml-12"}`}>
          <SourceList sources={sources} isArabic={isArabic} />
        </div>
      )}
      {suggestedActionlist && suggestedActionlist.length > 0 && (
        <div className={`${isArabic ? "mr-12" : "ml-12"}`}>
          <SuggestionActionList
            suggestions={suggestedActionlist}
            onSuggestionClick={onSuggestionClick}
            isArabic={isArabic}
            locked={locked}
          />
        </div>
      )}
    </li>
  );
};


export default MessageItem;
